import round from 'lodash/round';
import { isNotNil } from '@flowio/is';
import { currencies } from '@flowio/lib-reference-javascript';

export const TAX_CALCULATOR_URL = 'https://globale-smb--calculator-playground.retool.app/';

type Levy = io.flow.v0.enums.LevyInclusion;

export interface TaxCalculatorLine {
  hs_code: string;
  quantity: number;
  unit_amount?: number;
  country_of_origin?: string;
}

export interface TaxCalculatorRequest {
  destination?: string;
  postalCode?: string;
  province?: string;
  currency: string;
  shipFrom?: string;
  shipFromPostal?: string;
  shippingCharge?: number;
  inclusiveOf?: Levy[];
  lines: TaxCalculatorLine[];
}

type ComponentKey = io.flow.v0.enums.OrderPriceDetailComponentKey;
type AllocationComponent = io.flow.v0.unions.AllocationComponent;
type AllocationDetailComponents = Pick<io.flow.v0.models.AllocationLineDetail, 'included' | 'not_included'>;

const LEVIES: Levy[] = ['tax', 'duty'];

const LEVY_BY_COMPONENT_KEY: Partial<Record<ComponentKey, Levy>> = {
  vat_item_price: 'tax',
  vat_duties_item_price: 'tax',
  duties_item_price: 'duty',
};

const DISCOUNT_KEYS: ComponentKey[] = ['item_discount', 'order_discount'];

const isDiscount = (component: AllocationComponent): boolean => (
  DISCOUNT_KEYS.includes(component.key)
);

const isLineDetail = (
  detail: io.flow.v0.unions.AllocationDetail,
): detail is io.flow.v0.models.AllocationLineDetail => detail.discriminator === 'allocation_line_detail';

const isShippingDetail = (
  detail: io.flow.v0.unions.AllocationDetail,
): detail is io.flow.v0.models.AllocationOrderDetail => (
  detail.discriminator === 'allocation_order_detail' && detail.key === 'shipping'
);

const isPhysicalDelivery = (
  delivery: io.flow.v0.unions.Delivery,
): delivery is io.flow.v0.models.PhysicalDelivery => delivery.discriminator === 'physical_delivery';

function findLineDetail(
  allocations: io.flow.v0.models.AllocationV2 | undefined,
  item: io.flow.v0.models.LocalizedLineItem,
): io.flow.v0.models.AllocationLineDetail | undefined {
  return (allocations?.details ?? [])
    .filter(isLineDetail)
    .find((detail) => (
      (isNotNil(item.id) && detail.id === item.id) || detail.number === item.number
    ));
}

// The `included` components are the ones folded into the price the customer saw,
// so they define both the inclusivity and the customer-facing price.
function includedLevies(lineDetail: io.flow.v0.models.AllocationLineDetail): Levy[] {
  const levies = new Set(
    lineDetail.included.map((component) => LEVY_BY_COMPONENT_KEY[component.key]),
  );
  return LEVIES.filter((levy) => levies.has(levy));
}

function sumAmounts(
  components: AllocationComponent[],
  amountOf: (component: AllocationComponent) => number,
): number {
  return components.reduce((sum, component) => sum + amountOf(component), 0);
}

// Discounts sit in `not_included` as negative amounts; adding them gives what the customer paid.
function customerFacingAmount(
  detail: AllocationDetailComponents,
  amountOf: (component: AllocationComponent) => number,
): number {
  const discounts = detail.not_included.filter(isDiscount);
  return sumAmounts(detail.included, amountOf) + sumAmounts(discounts, amountOf);
}

function customerUnitAmount(
  lineDetail: io.flow.v0.models.AllocationLineDetail,
  precision: number,
): number {
  const unitAmount = (component: AllocationComponent): number => (
    component.price?.amount ?? component.total.amount / lineDetail.quantity
  );
  return round(customerFacingAmount(lineDetail, unitAmount), precision);
}

function shippingCharge(
  order: io.flow.v0.models.Order,
  allocations: io.flow.v0.models.AllocationV2 | undefined,
  precision: number,
): number | undefined {
  const shippingDetail = (allocations?.details ?? []).find(isShippingDetail);
  return shippingDetail
    ? round(customerFacingAmount(shippingDetail, (component) => component.total.amount), precision)
    : order.prices.find((price) => price.key === 'shipping')?.amount;
}

function leviesFromSettings(
  setting: io.flow.v0.enums.TaxAndDutyInclusivitySetting | undefined,
): Levy[] | undefined {
  switch (setting) {
    case 'duty_inclusive_tax_inclusive': return ['tax', 'duty'];
    case 'duty_exclusive_tax_inclusive': return ['tax'];
    case 'duty_inclusive_tax_exclusive': return ['duty'];
    case 'duty_exclusive_tax_exclusive': return [];
    default: return undefined;
  }
}

// Digital goods are not shipped and carry no duty, so the calculator has nothing to quote for them.
const isPhysicalGood = (item: io.flow.v0.models.LocalizedLineItem): boolean => (
  item.attributes?.digital_good !== 'true'
);

function providedUnitAmount(item: io.flow.v0.models.LocalizedLineItem): number | undefined {
  return item.price_source?.discriminator === 'provided' ? item.price_source.price.amount : undefined;
}

export function buildTaxCalculatorRequest(
  order: io.flow.v0.models.Order,
  allocations: io.flow.v0.models.AllocationV2 | undefined,
): TaxCalculatorRequest {
  const precision = currencies.find(order.total.currency)?.number_decimals ?? 2;
  const items = order.items.filter(isPhysicalGood);
  const lineDetails = items.map((item) => findLineDetail(allocations, item));
  const firstLineDetail = lineDetails.find(isNotNil);
  const shipFromLocation = order.deliveries.find(isPhysicalDelivery)?.center?.address?.location;
  const taxAndDutyInclusivitySetting = order.attributes?.tax_and_duty_inclusivity_setting as
    io.flow.v0.enums.TaxAndDutyInclusivitySetting | undefined;

  return {
    destination: order.destination.country,
    postalCode: order.destination.postal,
    province: order.destination.province,
    currency: order.total.currency,
    shipFrom: shipFromLocation?.country,
    shipFromPostal: shipFromLocation?.postal,
    shippingCharge: shippingCharge(order, allocations, precision),
    inclusiveOf: firstLineDetail
      ? includedLevies(firstLineDetail)
      : leviesFromSettings(taxAndDutyInclusivitySetting),
    lines: items.map((item, index) => {
      const lineDetail = lineDetails[index];
      return {
        hs_code: item.attributes?.hs_code ?? '',
        quantity: item.quantity,
        unit_amount: lineDetail
          ? customerUnitAmount(lineDetail, precision)
          : providedUnitAmount(item),
        country_of_origin: item.attributes?.country_of_origin,
      };
    }),
  };
}

export default function buildTaxCalculatorUrl(
  order: io.flow.v0.models.Order,
  allocations: io.flow.v0.models.AllocationV2 | undefined,
): string {
  const request = buildTaxCalculatorRequest(order, allocations);
  return `${TAX_CALCULATOR_URL}?request=${encodeURIComponent(JSON.stringify(request))}`;
}
